import { useMemo, type ReactNode } from 'react'
import { TONE_CLASS, type Tone } from '@/lib/status'
import { dayKey, dayLabel, formatRelative, parseTime } from '@/lib/time'
import { cn } from '@/lib/utils'

/** An item's dot on the rail. */
export interface TimelineMark {
  tone: Tone
  /** The dot blinks: the item is under way right now. */
  pulse?: boolean
}

export interface TimelineListProps<T> {
  items: T[]
  /** The item's place on the timeline (UTC ISO). Items without one are left
   *  out — a list that has them shows them in a lane of its own. */
  getTime: (item: T) => string | null | undefined
  getKey: (item: T) => string
  renderItem: (item: T) => ReactNode
  /** Names the list for screen readers. */
  label: string
  /** Shown when no item has a time. */
  empty?: ReactNode
  now?: Date
  /** Draws each day as a rail: the time and a dot in the item's tone down the
   *  left, the items under the part of the day they fall in, and today marked
   *  where now is. The rows then leave their own time out. */
  getMark?: (item: T) => TimelineMark
  className?: string
}

/** Parts of the day the rail labels, by the hour each starts at. */
const BANDS = [
  { from: 0, label: '새벽' },
  { from: 6, label: '오전' },
  { from: 12, label: '오후' },
  { from: 18, label: '저녁' },
]
const bandOf = (d: Date) => BANDS.filter((b) => d.getHours() >= b.from).length - 1

const pad = (n: number) => String(n).padStart(2, '0')
const hm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`

/** How soon an upcoming item is told under its time; further off, the day
 *  heading says enough. */
const SOON_MS = 24 * 3_600_000

/** Items grouped by calendar day in the browser's zone, each day under its
 *  heading ("오늘 10. 17. (금)", "내일 …", "10. 19. (일)"), in time order. */
export function TimelineList<T>({ items, getTime, getKey, renderItem, label, empty, now, getMark, className }: TimelineListProps<T>) {
  const days = useMemo(() => {
    const byDay = new Map<string, { date: Date; rows: { t: number; item: T }[] }>()
    for (const item of items) {
      const d = parseTime(getTime(item))
      if (!d) continue
      const key = dayKey(d)
      const g = byDay.get(key) ?? { date: d, rows: [] }
      g.rows.push({ t: d.getTime(), item })
      byDay.set(key, g)
    }
    return [...byDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, g]) => ({ key, date: g.date, rows: g.rows.sort((x, y) => x.t - y.t) }))
  }, [items, getTime])

  if (days.length === 0) return <>{empty ?? null}</>
  const ref = now ?? new Date()
  return (
    <div role="list" aria-label={label} className={cn('space-y-4', className)}>
      {days.map((d) => {
        const heading = dayLabel(d.date, ref)
        return (
          <section key={d.key} role="listitem" aria-label={heading} className="space-y-1.5">
            <h3
              className={cn(
                'sticky top-0 z-[1] -mx-1 bg-background/95 px-1 py-1 text-meta font-semibold backdrop-blur',
                d.key === dayKey(ref) ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {heading}
              <span className="ml-1.5 font-normal tabular-nums text-muted-foreground">{d.rows.length}</span>
            </h3>
            {getMark ? (
              <RailDay
                rows={d.rows}
                today={d.key === dayKey(ref)}
                now={ref}
                getTime={getTime}
                getKey={getKey}
                getMark={getMark}
                renderItem={renderItem}
              />
            ) : (
              <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
                {d.rows.map(({ item }) => (
                  <li key={getKey(item)}>{renderItem(item)}</li>
                ))}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}

/** The rail's three columns: the time, the line with the dot, the row. */
const RAIL_ROW = 'grid grid-cols-[3.25rem_1.5rem_minmax(0,1fr)] sm:grid-cols-[3.75rem_1.75rem_minmax(0,1fr)]'

function Line() {
  return <span aria-hidden className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border" />
}

/** One day on the rail. Each part of the day gets a label before its first
 *  item, and today a 지금 line between what is done and what is next. */
function RailDay<T>({
  rows,
  today,
  now,
  getTime,
  getKey,
  getMark,
  renderItem,
}: {
  rows: { t: number; item: T }[]
  today: boolean
  now: Date
  getTime: (item: T) => string | null | undefined
  getKey: (item: T) => string
  getMark: (item: T) => TimelineMark
  renderItem: (item: T) => ReactNode
}) {
  const perBand = BANDS.map((_, b) => rows.filter((r) => bandOf(new Date(r.t)) === b).length)
  const at = now.getTime()
  // Where 지금 goes: before the first row still ahead, after the last if
  // none is; not at all on another day.
  const nowAt = today ? rows.findIndex((r) => r.t > at) : -2
  const out: ReactNode[] = []
  let band = -1
  const label = (b: number) => {
    band = b
    out.push(
      <li key={`band-${b}`} aria-hidden className={RAIL_ROW}>
        <span />
        <span className="relative">
          <Line />
        </span>
        <span className="pb-1 pt-2 text-micro font-semibold text-muted-foreground">
          {BANDS[b].label}
          <span className="ml-1 font-normal tabular-nums">{perBand[b]}건</span>
        </span>
      </li>,
    )
  }
  const nowLine = () =>
    out.push(
      <li key="now" role="separator" aria-label={`지금 ${hm(now)}`} className={cn(RAIL_ROW, 'items-center py-1')}>
        <span className="text-right text-micro font-semibold tabular-nums text-accent">{hm(now)}</span>
        <span className="relative flex h-4 items-center justify-center">
          <Line />
          <span className="relative h-2 w-2 rounded-full bg-accent ring-4 ring-background" />
        </span>
        <span className="flex items-center gap-1.5">
          <span className="text-micro font-semibold text-accent">지금</span>
          <span className="h-px flex-1 bg-accent/60" />
        </span>
      </li>,
    )

  rows.forEach(({ t, item }, i) => {
    const b = bandOf(new Date(t))
    if (i === nowAt) {
      // Now falls in the part of the day that comes next: label it first.
      if (bandOf(now) === b && b !== band) label(b)
      nowLine()
    }
    if (b !== band) label(b)
    const mark = getMark(item)
    const ahead = t > at
    out.push(
      <li key={getKey(item)} className={RAIL_ROW}>
        <div className="pr-0.5 pt-3 text-right">
          <time
            dateTime={getTime(item) ?? undefined}
            className={cn('block text-table font-semibold tabular-nums', !ahead && 'text-muted-foreground')}
          >
            {hm(new Date(t))}
          </time>
          {ahead && t - at < SOON_MS && (
            <span className="block text-micro tabular-nums text-muted-foreground">
              {formatRelative(new Date(t).toISOString(), now)}
            </span>
          )}
        </div>
        <div className="relative flex justify-center">
          <Line />
          <span
            className={cn(
              'relative mt-[17px] h-2.5 w-2.5 rounded-full ring-4 ring-background',
              TONE_CLASS[mark.tone].dot,
              mark.pulse && 'animate-status-blink',
            )}
          />
        </div>
        <div className="min-w-0 pb-2">
          <div className="overflow-hidden rounded-lg border border-border bg-card">{renderItem(item)}</div>
        </div>
      </li>,
    )
  })
  if (today && nowAt === -1) nowLine()

  return <ul>{out}</ul>
}
